import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { EMPTY, distinctUntilChanged, interval, map, startWith, switchMap, tap, timer } from "rxjs";

type WindowAnimationProfile = {
  cycleMs: number;
  activeWindowCount: number;
};

@Component({
    selector: "[window-type-1]",
    templateUrl: "./window-type-1.component.html",
    styleUrls: ["./window-type-1.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
 export class WindowType1Component {
  private readonly destroyRef = inject(DestroyRef);

  public height = input<number>();
  public finalHeight = input<number>();

  public activeWindowIds = signal(new Set<string>());
  private readonly profile1: WindowAnimationProfile = { cycleMs: 5000, activeWindowCount: 84 };
  private readonly profile2: WindowAnimationProfile = { cycleMs: 7000, activeWindowCount: 84 };
  private readonly profile3: WindowAnimationProfile = { cycleMs: 9000, activeWindowCount: 36 };
  private readonly profile4: WindowAnimationProfile = { cycleMs: 0, activeWindowCount: 0 };
  
  public windowWidth: number = 30;
  public windowHeight: number = 31;

  public leftWindowPositions = computed(() => {
    if(this.height() === this.finalHeight()) {
      return this.generateWindows(6, this.height(), {
        xStart: 3,
        yStart: 130,
        rowMargin: 18,
        extraColumnMargin: 30,
        extraColumnMarginAfter9: 35,
        adjustRowMargin: true,
      })
    }
    return [];
  });

  public rightWindowPositions = computed(() => {
    if(this.height() === this.finalHeight()) {
      return this.generateWindows(6, this.height(), {
        xStart: 190,
        yStart: 220,
        rowMargin: 18,
        extraColumnMargin: 30,
        extraColumnMarginAfter9: 35,
        adjustRowMargin: false,
      })
    }
    return [];

  });

  ngOnInit(): void {
    timer(0, 1000)
      .pipe(
        map((elapsedSeconds) => elapsedSeconds * 1000),
        map((elapsedMs) => this.getProfileForElapsedMs(elapsedMs)),
        distinctUntilChanged(),
        switchMap((profile) => {
          if (profile === this.profile4) {
            this.activeWindowIds.set(new Set());
            return EMPTY;
          }

          return interval(profile.cycleMs).pipe(
            startWith(0),
            tap(() => {
              this.activeWindowIds.set(this.pickRandomWindows(profile.activeWindowCount));
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getProfileForElapsedMs(elapsedMs: number): WindowAnimationProfile {
    if (elapsedMs >= 15 * 60 * 1000) {
      return this.profile4;
    }

    if (elapsedMs >= 5 * 60 * 1000) {
      return this.profile3;
    }

    if (elapsedMs >= 60 * 1000) {
      return this.profile2;
    }

    return this.profile1;
  }

  public isWindowLit(columnIndex: number, rowIndex: number, isLeft: boolean): boolean {
    return this.activeWindowIds().has(this.getWindowId(columnIndex, rowIndex, isLeft));
  }

  private getWindowId(columnIndex: number, rowIndex: number, isLeft: boolean): string {
    return `${isLeft ? "left" : "right"}-${columnIndex}-${rowIndex}`;
  }

  private pickRandomWindows(count: number): Set<string> {
    const leftRows = this.leftWindowPositions();
    const rightRows = this.rightWindowPositions();
    const leftTotal = leftRows.flat().length;
    const rightTotal = rightRows.flat().length;
    const totalWindows = leftTotal + rightTotal;
    const selectedIndices = new Set<number>();

    while (selectedIndices.size < Math.min(count, totalWindows)) {
      selectedIndices.add(Math.floor(Math.random() * totalWindows));
    }

    const nextStates = new Set<string>();

    selectedIndices.forEach((index) => {
      const isLeft = index < leftTotal;
      const localIndex = isLeft ? index : index - leftTotal;
      const rows = isLeft ? leftRows : rightRows;
      const rowCount = rows[0]?.length ?? 0;

      if (!rowCount) {
        return;
      }

      const columnIndex = Math.floor(localIndex / rowCount);
      const rowIndex = localIndex % rowCount;
      nextStates.add(this.getWindowId(columnIndex, rowIndex, isLeft));
    });

    return nextStates;
  }

  private generateWindows(
    columns: number,
    initialHeight: number,
    config: {
      xStart: number;
      yStart: number;
      rowMargin: number;
      extraColumnMargin: number;
      extraColumnMarginAfter9: number;
      adjustRowMargin: boolean;
    }
  ) {
    const { xStart, yStart, rowMargin, extraColumnMargin, extraColumnMarginAfter9, adjustRowMargin } = config;
    const rowHeight = 18;
    const rows = Math.floor(initialHeight / (rowHeight + rowMargin));
    return Array.from({ length: columns }, (_, column) =>
      Array.from({ length: rows }, (_, row) => {
        const x = xStart + column * this.windowWidth;
        let y = yStart + row * rowHeight;
        if (adjustRowMargin && column > 0) {
          y += column * rowMargin;
        } else if (!adjustRowMargin && column > 0) {
          y -= column * rowMargin;
        }
        if (row > 2) {
          y += extraColumnMargin;
        }
        if (row > 8) {
          y += extraColumnMarginAfter9;
        }
        return { x, y };
      })
    );
  }

  public trackByIndex = (index: number) => index;
}
